import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HostelSetting = () => {
  const [hostels, setHostels] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHostel, setEditingHostel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ hostel_name: '', hostel_code: '', hostel_type: 'Boys', address: '', total_rooms: 0, total_capacity: 0, contact_number: '', email: '', facilities: '' });

  useEffect(() => { fetchHostels(); }, []);

  const fetchHostels = async () => {
    try {
      const response = await apiClient.get('/api/hostel/hostels');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setHostels(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = editingHostel ? `/api/hostel/hostels/${editingHostel.id}` : `/api/hostel/hostels`;
      const response = editingHostel 
        ? await apiClient.put(url, formData) 
        : await apiClient.post(url, formData);
      if (response.status === 200 || response.status === 201 || response.data) {
        alert('Hostel saved successfully!');
        fetchHostels();
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error saving hostel:', error);
      alert('Failed to save hostel: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this hostel?')) {
      try {
        await apiClient.delete(`/api/hostel/hostels/${id}`);
        fetchHostels();
        alert('Hostel deleted successfully!');
      } catch (error) {
        alert('Failed to delete: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleEdit = (hostel) => {
    setEditingHostel(hostel);
    setFormData({ hostel_name: hostel.hostel_name, hostel_code: hostel.hostel_code, hostel_type: hostel.hostel_type, address: hostel.address, total_rooms: hostel.total_rooms, total_capacity: hostel.total_capacity, contact_number: hostel.contact_number, email: hostel.email || '', facilities: hostel.facilities || '' });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingHostel(null);
    setFormData({ hostel_name: '', hostel_code: '', hostel_type: 'Boys', address: '', total_rooms: 0, total_capacity: 0, contact_number: '', email: '', facilities: '' });
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Hostel Settings</CardTitle>
          <Button onClick={() => setIsModalOpen(true)}><Plus className="w-4 h-4 mr-2" />Add Hostel</Button>
        </CardHeader>
        <CardContent>
          <table className="w-full border-collapse">
            <thead><tr className="border-b"><th className="text-left p-3">Name</th><th className="text-left p-3">Code</th><th className="text-left p-3">Type</th><th className="text-left p-3">Rooms</th><th className="text-left p-3">Capacity</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Actions</th></tr></thead>
            <tbody>
              {hostels.length === 0 ? <tr><td colSpan="7" className="text-center p-4 text-gray-500">No hostels found.</td></tr> : hostels.map((hostel) => (
                <tr key={hostel.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">{hostel.hostel_name}</td>
                  <td className="p-3">{hostel.hostel_code}</td>
                  <td className="p-3"><span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{hostel.hostel_type}</span></td>
                  <td className="p-3">{hostel.total_rooms}</td>
                  <td className="p-3">{hostel.total_capacity}</td>
                  <td className="p-3">{hostel.contact_number}</td>
                  <td className="p-3"><div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => handleEdit(hostel)}><Edit className="w-3 h-3" /></Button><Button size="sm" variant="destructive" onClick={() => handleDelete(hostel.id)}><Trash2 className="w-3 h-3" /></Button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingHostel ? 'Edit' : 'Add'} Hostel</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4"><div><Label>Hostel Name *</Label><Input required value={formData.hostel_name} onChange={(e) => setFormData({ ...formData, hostel_name: e.target.value })} /></div><div><Label>Code *</Label><Input required value={formData.hostel_code} onChange={(e) => setFormData({ ...formData, hostel_code: e.target.value })} /></div></div>
            <div><Label>Type *</Label><Select value={formData.hostel_type} onValueChange={(value) => setFormData({ ...formData, hostel_type: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Boys">Boys</SelectItem><SelectItem value="Girls">Girls</SelectItem><SelectItem value="Mixed">Mixed</SelectItem></SelectContent></Select></div>
            <div><Label>Address *</Label><Input required value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4"><div><Label>Total Rooms *</Label><Input type="number" required value={formData.total_rooms} onChange={(e) => setFormData({ ...formData, total_rooms: parseInt(e.target.value) })} /></div><div><Label>Total Capacity *</Label><Input type="number" required value={formData.total_capacity} onChange={(e) => setFormData({ ...formData, total_capacity: parseInt(e.target.value) })} /></div></div>
            <div className="grid grid-cols-2 gap-4"><div><Label>Contact *</Label><Input required value={formData.contact_number} onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })} /></div><div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div></div>
            <div><Label>Facilities</Label><Input value={formData.facilities} onChange={(e) => setFormData({ ...formData, facilities: e.target.value })} placeholder="WiFi, Mess, etc." /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button><Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HostelSetting;